import { useState } from 'react';
import axios from 'axios';
import { FiSearch, FiEdit, FiPlus, FiX, FiGitMerge } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddBookTab({
  addBookMode, setAddBookMode, isbn, setIsbn, scanning, scanResult, handleScanISBN,
  manualBook, setManualBook, addingBook, bookResult, handleAddBook, navigate,
  selectedAuthors, setSelectedAuthors, selectedCategories, setSelectedCategories,
  authorInput, setAuthorInput, categoryInput, setCategoryInput,
  showAuthorDropdown, setShowAuthorDropdown, showCategoryDropdown, setShowCategoryDropdown,
  allAuthors, allCategories, authorDropdownRef, categoryDropdownRef,
  vendorInput, setVendorInput, showVendorDropdown, setShowVendorDropdown, allVendors, vendorDropdownRef,
  getAuthHeader, fetchAuthorsAndCategories
}) {

  // Genre merge state
  const [mergeSourceGenres, setMergeSourceGenres] = useState([]);
  const [mergeTargetGenre, setMergeTargetGenre] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const handleMergeGenres = async () => {
    if (mergeSourceGenres.length === 0 || !mergeTargetGenre.trim()) return;
    setMerging(true);
    setMergeResult(null);
    try {
      const response = await axios.post(`${API}/admin/genres/merge`, {
        source_genres: mergeSourceGenres,
        target_genre: mergeTargetGenre.trim(),
        delete_sources: true
      }, { headers: getAuthHeader() });
      setMergeResult({ success: true, data: response.data });
      setMergeSourceGenres([]);
      setMergeTargetGenre('');
      if (fetchAuthorsAndCategories) fetchAuthorsAndCategories();
    } catch (error) {
      setMergeResult({ success: false, error: error.response?.data?.detail || 'Failed to merge genres' });
    } finally {
      setMerging(false);
    }
  };

  return (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Add Books</h2>
              
              {/* Mode Toggle */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setAddBookMode('isbn')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    addBookMode === 'isbn'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="add-book-isbn-mode"
                >
                  <FiSearch className="w-4 h-4" />
                  Scan ISBN
                </button>
                <button
                  onClick={() => setAddBookMode('manual')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    addBookMode === 'manual'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="add-book-manual-mode"
                >
                  <FiEdit className="w-4 h-4" />
                  Manual Entry
                </button>
              </div>

              {/* ISBN Scan Mode */}
              {addBookMode === 'isbn' && (
                <div>
                  <p className="text-gray-600 mb-6">
                    Enter the ISBN of a book to automatically fetch its details from Google Books and Open Library.
                  </p>

                  <form onSubmit={handleScanISBN} className="mb-6">
                    <div className="flex gap-4">
                      <input
                        type="text"
                        value={isbn}
                        onChange={(e) => setIsbn(e.target.value)}
                        placeholder="Enter ISBN (e.g., 9780141439518)"
                        className="flex-grow px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                        data-testid="isbn-input"
                      />
                      <button
                        type="submit"
                        disabled={scanning}
                        className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                        data-testid="scan-isbn-button"
                      >
                        <FiPlus className="w-5 h-5" />
                        <span>{scanning ? 'Scanning...' : 'Add Book'}</span>
                      </button>
                    </div>
                  </form>

                  {scanResult && (
                    <div
                      className={`p-6 rounded-lg ${
                        scanResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                      }`}
                      data-testid="scan-result"
                    >
                      {scanResult.success ? (
                        <div>
                          <h3 className="text-lg font-semibold text-green-800 mb-4">Book Added Successfully!</h3>
                          <div className="flex gap-4">
                            {scanResult.book.cover_image_url && (
                              <img
                                src={scanResult.book.cover_image_url}
                                alt={scanResult.book.title}
                                className="w-24 h-36 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-semibold text-gray-800">{scanResult.book.title}</p>
                              <p className="text-gray-600 text-sm">by {scanResult.book.authors.join(', ')}</p>
                              <p className="text-gray-600 text-sm mt-2">ISBN: {scanResult.book.isbn}</p>
                              <button
                                onClick={() => navigate(`/book/${scanResult.book.id}`)}
                                className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                              >
                                View Book Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
                          <p className="text-red-700">{scanResult.error}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Entry Mode */}
              {addBookMode === 'manual' && (
                <div>
                  <p className="text-gray-600 mb-6">
                    Add a book to the catalog by entering its details manually.
                  </p>

                  <form onSubmit={handleAddBook} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input
                        type="text"
                        value={manualBook.title}
                        onChange={(e) => setManualBook({...manualBook, title: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                        placeholder="Enter book title"
                        required
                        data-testid="manual-book-title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Authors *</label>
                      <div ref={authorDropdownRef} className="relative">
                        {/* Selected author tags */}
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {selectedAuthors.map((author, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                              {author}
                              <button type="button" onClick={() => setSelectedAuthors(selectedAuthors.filter((_, i) => i !== idx))} className="hover:text-red-600">
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          value={authorInput}
                          onChange={(e) => { setAuthorInput(e.target.value); setShowAuthorDropdown(true); }}
                          onFocus={() => setShowAuthorDropdown(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && authorInput.trim()) {
                              e.preventDefault();
                              if (!selectedAuthors.includes(authorInput.trim())) {
                                setSelectedAuthors([...selectedAuthors, authorInput.trim()]);
                              }
                              setAuthorInput('');
                              setShowAuthorDropdown(false);
                            }
                          }}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder={selectedAuthors.length > 0 ? "Add another author..." : "Type to search or add new author"}
                          data-testid="manual-book-authors"
                        />
                        {showAuthorDropdown && authorInput.trim() && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {allAuthors
                              .filter(a => a.toLowerCase().includes(authorInput.toLowerCase()) && !selectedAuthors.includes(a))
                              .slice(0, 10)
                              .map((author, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => { setSelectedAuthors([...selectedAuthors, author]); setAuthorInput(''); setShowAuthorDropdown(false); }}
                                  className="w-full text-left px-4 py-2 hover:bg-amber-50 text-sm text-gray-700 border-b border-gray-100 last:border-0"
                                >
                                  {author}
                                </button>
                              ))}
                            {authorInput.trim() && !allAuthors.some(a => a.toLowerCase() === authorInput.toLowerCase()) && (
                              <button
                                type="button"
                                onClick={() => { setSelectedAuthors([...selectedAuthors, authorInput.trim()]); setAuthorInput(''); setShowAuthorDropdown(false); }}
                                className="w-full text-left px-4 py-2 hover:bg-green-50 text-sm text-green-700 font-medium"
                              >
                                + Add "{authorInput.trim()}"
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                        <input
                          type="text"
                          value={manualBook.isbn}
                          onChange={(e) => setManualBook({...manualBook, isbn: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder="ISBN (optional)"
                          data-testid="manual-book-isbn"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Copies</label>
                        <input
                          type="number"
                          min="1"
                          value={manualBook.total_copies}
                          onChange={(e) => setManualBook({...manualBook, total_copies: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          data-testid="manual-book-copies"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
                        <input
                          type="text"
                          value={manualBook.publisher}
                          onChange={(e) => setManualBook({...manualBook, publisher: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder="Publisher name"
                          data-testid="manual-book-publisher"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Published Date</label>
                        <input
                          type="text"
                          value={manualBook.published_date}
                          onChange={(e) => setManualBook({...manualBook, published_date: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder="e.g., 2023"
                          data-testid="manual-book-date"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Available From (Coming Soon)
                        <span className="text-xs text-gray-500 ml-2">Leave empty if book is available now</span>
                      </label>
                      <input
                        type="date"
                        value={manualBook.available_from}
                        onChange={(e) => setManualBook({...manualBook, available_from: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                        data-testid="manual-book-available-from"
                      />
                      {manualBook.available_from && new Date(manualBook.available_from) > new Date() && (
                        <p className="mt-1 text-sm text-purple-600">
                          This book will show as "Coming Soon" until {new Date(manualBook.available_from).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categories/Genres</label>
                      <div ref={categoryDropdownRef} className="relative">
                        {/* Selected category tags */}
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {selectedCategories.map((cat, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                              {cat}
                              <button type="button" onClick={() => setSelectedCategories(selectedCategories.filter((_, i) => i !== idx))} className="hover:text-red-600">
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          value={categoryInput}
                          onChange={(e) => { setCategoryInput(e.target.value); setShowCategoryDropdown(true); }}
                          onFocus={() => setShowCategoryDropdown(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && categoryInput.trim()) {
                              e.preventDefault();
                              if (!selectedCategories.includes(categoryInput.trim())) {
                                setSelectedCategories([...selectedCategories, categoryInput.trim()]);
                              }
                              setCategoryInput('');
                              setShowCategoryDropdown(false);
                            }
                          }}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder={selectedCategories.length > 0 ? "Add another category..." : "Type to search or add new category"}
                          data-testid="manual-book-categories"
                        />
                        {showCategoryDropdown && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {allCategories
                              .filter(c => (!categoryInput.trim() || c.toLowerCase().includes(categoryInput.toLowerCase())) && !selectedCategories.includes(c))
                              .slice(0, 15)
                              .map((cat, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => { setSelectedCategories([...selectedCategories, cat]); setCategoryInput(''); setShowCategoryDropdown(false); }}
                                  className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm text-gray-700 border-b border-gray-100 last:border-0"
                                >
                                  {cat}
                                </button>
                              ))}
                            {categoryInput.trim() && !allCategories.some(c => c.toLowerCase() === categoryInput.toLowerCase()) && (
                              <button
                                type="button"
                                onClick={() => { setSelectedCategories([...selectedCategories, categoryInput.trim()]); setCategoryInput(''); setShowCategoryDropdown(false); }}
                                className="w-full text-left px-4 py-2 hover:bg-green-50 text-sm text-green-700 font-medium"
                              >
                                + Add "{categoryInput.trim()}"
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL</label>
                      <input
                        type="url"
                        value={manualBook.cover_image_url}
                        onChange={(e) => setManualBook({...manualBook, cover_image_url: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                        placeholder="https://example.com/cover.jpg"
                        data-testid="manual-book-cover"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={manualBook.description}
                        onChange={(e) => setManualBook({...manualBook, description: e.target.value})}
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                        placeholder="Book description..."
                        data-testid="manual-book-description"
                      />
                    </div>

                    {/* Procurement Details Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Procurement Details (Optional)</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Procurement Type</label>
                          <select
                            value={manualBook.procurement_type}
                            onChange={(e) => setManualBook({...manualBook, procurement_type: e.target.value})}
                            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                            data-testid="manual-book-procurement-type"
                          >
                            <option value="purchased">Purchased</option>
                            <option value="donated">Donated</option>
                            <option value="sponsored">Sponsored</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price (₹)</label>
                          <input
                            type="number"
                            value={manualBook.purchase_price}
                            onChange={(e) => setManualBook({...manualBook, purchase_price: e.target.value})}
                            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                            placeholder="0"
                            data-testid="manual-book-purchase-price"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor/Donor</label>
                          <div ref={vendorDropdownRef} className="relative">
                            <input
                              type="text"
                              value={vendorInput}
                              onChange={(e) => { setVendorInput(e.target.value); setShowVendorDropdown(true); }}
                              onFocus={() => setShowVendorDropdown(true)}
                              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                              placeholder="Type to search or add new"
                              data-testid="manual-book-vendor"
                            />
                            {showVendorDropdown && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                {allVendors
                                  .filter(v => (!vendorInput.trim() || v.toLowerCase().includes(vendorInput.toLowerCase())))
                                  .slice(0, 10)
                                  .map((v, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => { setVendorInput(v); setShowVendorDropdown(false); }}
                                      className="w-full text-left px-4 py-2 hover:bg-purple-50 text-sm text-gray-700 border-b border-gray-100 last:border-0"
                                    >
                                      {v}
                                    </button>
                                  ))}
                                {vendorInput.trim() && !allVendors.some(v => v.toLowerCase() === vendorInput.toLowerCase()) && (
                                  <button
                                    type="button"
                                    onClick={() => setShowVendorDropdown(false)}
                                    className="w-full text-left px-4 py-2 hover:bg-green-50 text-sm text-green-700 font-medium"
                                  >
                                    + Use "{vendorInput.trim()}"
                                  </button>
                                )}
                                {!vendorInput.trim() && allVendors.length === 0 && (
                                  <p className="px-4 py-2 text-sm text-gray-400 italic">No vendors/donors yet</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Procurement Date</label>
                          <input
                            type="date"
                            value={manualBook.procurement_date}
                            onChange={(e) => setManualBook({...manualBook, procurement_date: e.target.value})}
                            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                            data-testid="manual-book-procurement-date"
                          />
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Procurement Notes</label>
                        <input
                          type="text"
                          value={manualBook.procurement_notes}
                          onChange={(e) => setManualBook({...manualBook, procurement_notes: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                          placeholder="Any additional notes about procurement"
                          data-testid="manual-book-procurement-notes"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={addingBook}
                      className="w-full px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                      data-testid="add-book-submit"
                    >
                      {addingBook ? 'Adding Book...' : 'Add Book to Catalog'}
                    </button>
                  </form>

                  {bookResult && (
                    <div className={`mt-4 p-4 rounded-lg ${bookResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      {bookResult.success ? (
                        <p className="text-green-700 font-medium">{bookResult.message}</p>
                      ) : (
                        <p className="text-red-700">{bookResult.error}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Genre Merge Tool */}
            <div className="bg-white rounded-xl shadow-lg p-8 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <FiGitMerge className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-800">Merge Genres</h3>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                Select genres to merge into a single genre. All books with the selected genres will be updated.
              </p>

              {/* Source genres - select from existing */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Genres to merge (select one or more)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {mergeSourceGenres.map((g, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                      {g}
                      <button onClick={() => setMergeSourceGenres(mergeSourceGenres.filter((_, i) => i !== idx))} className="hover:text-red-600">
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
                  {allCategories
                    .filter(c => !mergeSourceGenres.includes(c))
                    .map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setMergeSourceGenres([...mergeSourceGenres, cat])}
                        className="px-3 py-1.5 bg-white border border-gray-300 hover:border-purple-400 hover:bg-purple-50 rounded-full text-sm text-gray-700 transition-colors"
                        data-testid={`merge-source-${cat}`}
                      >
                        {cat}
                      </button>
                    ))}
                </div>
              </div>

              {/* Target genre name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New genre name</label>
                <input
                  type="text"
                  value={mergeTargetGenre}
                  onChange={(e) => setMergeTargetGenre(e.target.value)}
                  placeholder="e.g. Mystery & Thriller"
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  data-testid="merge-target-genre"
                />
              </div>

              {/* Merge button */}
              <button
                onClick={handleMergeGenres}
                disabled={merging || mergeSourceGenres.length === 0 || !mergeTargetGenre.trim()}
                className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="merge-genres-btn"
              >
                <FiGitMerge className="w-4 h-4" />
                {merging ? 'Merging...' : `Merge ${mergeSourceGenres.length} Genre${mergeSourceGenres.length !== 1 ? 's' : ''}`}
              </button>

              {/* Result */}
              {mergeResult && (
                <div className={`mt-4 p-4 rounded-lg ${mergeResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="merge-result">
                  {mergeResult.success ? (
                    <div>
                      <p className="text-green-700 font-medium">{mergeResult.data.message}</p>
                      <p className="text-sm text-green-600 mt-1">{mergeResult.data.books_updated} book{mergeResult.data.books_updated !== 1 ? 's' : ''} updated</p>
                    </div>
                  ) : (
                    <p className="text-red-700">{mergeResult.error}</p>
                  )}
                </div>
              )}
            </div>
          </div>
  );
}
